import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Crown } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const DealsTab = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', revenue_share_pct: 80, duration_days: 0, bonus_tokens: 0, perks: '', max_claims: 0, is_public: false, target_username: '' });
  const [creating, setCreating] = useState(false);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/deals/list`);
      setDeals(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/deals/create`, {
        ...form,
        target_username: form.target_username || null,
      });
      toast.success(`Deal created! Share link: ${window.location.origin}${res.data.share_url}`);
      navigator.clipboard.writeText(`${window.location.origin}${res.data.share_url}`);
      setShowCreate(false);
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setCreating(false); }
  };

  const handleToggle = async (code) => {
    try {
      const res = await axios.post(`${API}/api/deals/${code}/toggle`);
      toast.success(res.data.is_active ? 'Deal activated' : 'Deal deactivated');
      fetchDeals();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading deals...</div>;

  return (
    <div data-testid="deals-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Creator Deals</h2>
        <button onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-amber-500 text-amber-950 rounded-full font-semibold text-sm hover:bg-amber-600 transition-colors flex items-center gap-1"
          data-testid="create-deal-btn">
          <Plus className="w-4 h-4" /> New Deal
        </button>
      </div>

      {showCreate && (
        <div className="bg-slate-900/80 border border-amber-500/20 rounded-2xl p-6 mb-6" data-testid="deal-form">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Deal Title *</label>
              <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                placeholder="e.g. Launch Special — 95% For Life" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" data-testid="deal-title-input" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Target Creator (optional)</label>
              <input type="text" value={form.target_username} onChange={e => setForm({...form, target_username: e.target.value})}
                placeholder="Leave empty for open deal" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Revenue Share %</label>
              <input type="number" value={form.revenue_share_pct} onChange={e => setForm({...form, revenue_share_pct: parseFloat(e.target.value)})}
                min={50} max={100} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white" data-testid="deal-revenue-input" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Duration (days, 0 = lifetime)</label>
              <input type="number" value={form.duration_days} onChange={e => setForm({...form, duration_days: parseInt(e.target.value) || 0})}
                min={0} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Bonus Tokens</label>
              <input type="number" value={form.bonus_tokens} onChange={e => setForm({...form, bonus_tokens: parseInt(e.target.value) || 0})}
                min={0} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Max Claims (0 = unlimited)</label>
              <input type="number" value={form.max_claims} onChange={e => setForm({...form, max_claims: parseInt(e.target.value) || 0})}
                min={0} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-sm text-slate-400 mb-1 block">Perks Description</label>
            <textarea value={form.perks} onChange={e => setForm({...form, perks: e.target.value})}
              placeholder="e.g. Featured on homepage, priority support" rows={2}
              className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 resize-none" />
          </div>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.is_public} onChange={e => setForm({...form, is_public: e.target.checked})}
                className="rounded bg-slate-800 border-white/10" />
              <span className="text-sm">Show on public flash deals page</span>
            </label>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowCreate(false)} className="px-6 py-2.5 border border-white/10 text-slate-400 rounded-full hover:text-white transition-colors">Cancel</button>
            <button onClick={handleCreate} disabled={creating}
              className="px-6 py-2.5 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
              data-testid="confirm-deal-btn">
              {creating ? 'Creating...' : 'Create Deal & Copy Link'}
            </button>
          </div>
        </div>
      )}

      {deals.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/50 border border-white/5 rounded-2xl">
          <Crown className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No deals yet. Create one to recruit creators!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deals.map(d => (
            <div key={d.deal_code} className={`bg-slate-900/80 border rounded-xl p-4 ${d.is_active ? 'border-amber-500/20' : 'border-white/5 opacity-60'}`} data-testid={`deal-${d.deal_code}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold">{d.title}</h3>
                    {d.is_public && <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">Public</span>}
                    {!d.is_active && <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-400 rounded-full">Inactive</span>}
                  </div>
                  {d.target_username && <p className="text-slate-500 text-xs">For: @{d.target_username}</p>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-400">{d.revenue_share_pct}%</p>
                  <p className="text-slate-500 text-xs">{d.duration_label}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                <span>{d.claims_count} claimed{d.max_claims > 0 ? ` / ${d.max_claims} max` : ''}</span>
                {d.bonus_tokens > 0 && <span className="text-amber-400">+{d.bonus_tokens} bonus tokens</span>}
                {d.perks && <span className="truncate">{d.perks}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/deals/${d.deal_code}`); toast.success('Link copied!'); }}
                  className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700 transition-colors">
                  Copy Link
                </button>
                <button onClick={() => handleToggle(d.deal_code)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${d.is_active ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`}>
                  {d.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealsTab;
